import { useState } from 'react';
import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';
import usePushNotifications from '../../utils/usePushNotifications';

/**
 * PushOptInButton — Cluster 6 · P7 enhancement.
 *
 * Tiny inline opt-in control that appears next to the LiveOrderStatus
 * badge. Three states:
 *   - unsupported  →  hidden (no button — silent fallback)
 *   - denied       →  greyed-out BellOff icon + tooltip
 *   - default      →  outlined Bell icon; click → prompt for permission,
 *                     register FCM token via /api/push/register
 *   - granted      →  filled BellRing icon; click → unregister
 *
 * Reuses the existing usePushNotifications hook (already wired to
 * firebase-messaging-sw.js + /api/push endpoints). No new endpoints,
 * no parallel service worker.
 */
export default function PushOptInButton({ orderId }) {
  const {
    isSupported,
    isEnabled,
    permission,
    loading,
    enableNotifications,
    disableNotifications,
  } = usePushNotifications();

  const [busy, setBusy] = useState(false);

  if (!isSupported) return null;

  const disabled = loading || busy || permission === 'denied';

  const onClick = async () => {
    setBusy(true);
    try {
      if (isEnabled) {
        await disableNotifications();
      } else {
        await enableNotifications();
      }
    } finally {
      setBusy(false);
    }
  };

  let icon = <Bell className="w-3.5 h-3.5" />;
  let title = 'Get a phone notification when this order is ready';
  let stateAttr = 'off';
  if (permission === 'denied') {
    icon = <BellOff className="w-3.5 h-3.5 text-slate-300" />;
    title = 'Notifications blocked in your browser settings';
    stateAttr = 'denied';
  } else if (isEnabled) {
    icon = <BellRing className="w-3.5 h-3.5 text-emerald-600" />;
    title = 'Push notifications on — click to turn off';
    stateAttr = 'on';
  }
  if (busy || loading) icon = <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      data-testid={`push-optin-btn-${orderId || 'inline'}`}
      data-push-state={stateAttr}
      className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white
                 hover:border-emerald-400 hover:bg-emerald-50 transition-colors
                 disabled:opacity-50 disabled:cursor-not-allowed
                 w-6 h-6 shadow-sm"
    >
      {icon}
    </button>
  );
}
